import { ref, provide, inject } from "vue";
import type { Ref, InjectionKey } from "vue";

import type {
  IBookingAddon,
  IBookingTicket,
} from "@/interfaces/store/IBooking";

type RetailRow = { id: number; price_name: string; quantity: number };

type DialogsApi = {
  ticketsOpen: Ref<boolean>;
  ticketsList: Ref<IBookingTicket[]>;
  openTickets: (list: IBookingTicket[]) => boolean;

  retailsOpen: Ref<boolean>;
  retailsList: Ref<RetailRow[]>;
  openRetails: (list: RetailRow[]) => boolean;

  addonsOpen: Ref<boolean>;
  addonsList: Ref<IBookingAddon[]>;
  openAddons: (list: IBookingAddon[]) => boolean;
};

const KEY: InjectionKey<DialogsApi> = Symbol("orders-dialogs");

/**
 * Provide three "show more" popup-modal slots — Tickets / Retails / Addons —
 * to descendant cells. The provider lives in OrdersSearchTable.vue and
 * mounts a single `<el-dialog>` per type that all rows reuse, instead of
 * each cell instantiating its own dialog (the pre-refactor pattern, which
 * scaled badly: 25 rows × 3 dialog types = 75 hidden dialog instances).
 *
 * Cells call `openTickets(list)` / `openRetails(list)` / `openAddons(list)`
 * with their pre-parsed view data; the provider sets the corresponding
 * `Ref<boolean>` to `true` and the list ref to the supplied value, and the
 * single `<el-dialog>` reactively renders.
 *
 * @returns {DialogsApi} Reactive open-state + list refs + open-functions.
 *   Also `provide`d under the internal injection key for cells to consume
 *   via `useOrdersDialogsInject`.
 */
export function useOrdersDialogsProvide(): DialogsApi {
  const ticketsOpen = ref(false);
  const ticketsList = ref<IBookingTicket[]>([]);

  const retailsOpen = ref(false);
  const retailsList = ref<RetailRow[]>([]);

  const addonsOpen = ref(false);
  const addonsList = ref<IBookingAddon[]>([]);

  /**
   * Show the tickets popup with the given list.
   *
   * @param {IBookingTicket[]} list - Tickets to render in the dialog.
   * @returns {boolean} Sentinel.
   */
  function openTickets(list: IBookingTicket[]): boolean {
    ticketsList.value = list;
    ticketsOpen.value = true;

    return true;
  }

  /**
   * Show the retails popup with the given list.
   *
   * @param {RetailRow[]} list - Retails to render in the dialog.
   * @returns {boolean} Sentinel.
   */
  function openRetails(list: RetailRow[]): boolean {
    retailsList.value = list;
    retailsOpen.value = true;

    return true;
  }

  /**
   * Show the addons popup with the given list.
   *
   * @param {IBookingAddon[]} list - Addons to render in the dialog.
   * @returns {boolean} Sentinel.
   */
  function openAddons(list: IBookingAddon[]): boolean {
    addonsList.value = list;
    addonsOpen.value = true;

    return true;
  }

  const api: DialogsApi = {
    ticketsOpen,
    ticketsList,
    openTickets,
    retailsOpen,
    retailsList,
    openRetails,
    addonsOpen,
    addonsList,
    openAddons,
  };

  provide(KEY, api);

  return api;
}

/**
 * Consume the dialogs API provided by an ancestor `useOrdersDialogsProvide`.
 * Throws if called outside the provider tree (programmer error).
 *
 * @returns {DialogsApi} Reactive open-state + open-functions from the provider.
 */
export function useOrdersDialogsInject(): DialogsApi {
  const api = inject(KEY);

  if (!api) {
    throw new Error(
      "useOrdersDialogsInject must be called inside an useOrdersDialogsProvide tree"
    );
  }

  return api;
}
